import { head } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { isUserAdmin, isUserManager, userHasRoleInpulse } from '@commons/utils/roles';

import sortActionsList from '@commons/utils/sortActionsList';

import {
  canDownloadProductTemplate,
  canImportProduct,
  canDownloadBatchUpdateProductTemplate,
  canImportBatchUpdateProduct,
} from '@selectors/actions/productActions';

import { Button } from '@commons/utils/styledLibraryComponents';
import {
  getManageCategoriesModalParams,
  getManageSubCategoriesModalParams,
  getAssignCategoriesModalParams,
  getAssignSubCategoriesModalParams,
} from '@commons/constants/categoryTypes';

import { getDeleteProductsModalParams, getDisableProductsModalParams } from './modalConfigurations';
import { isProductEnableable } from './enableProducts';

/**
 * The activation is disabled only when none of the selected products can be enabled
 *
 * @param {[object]} products selected products
 * @returns boolean
 */
const isEnableActionDisabled = (products) => !products.some(isProductEnableable);

export const getActions = (props) => {
  const {
    filteringOnActive,
    triggerCreate,
    selectedItems,
    triggerExport,
    triggerExportByStore,
    hasVariants,
    user,
    openGenericModal,
    handleProductsDeletion,
    disableProducts,
    getProductBatchCreationTemplate,
    getCentralKitchenProductBatchCreationTemplate,
    openProductsImportModal,
    triggerImportCentralKitchenProductsScope,
    triggerEnableProductModal,
    triggerEnableProductsModal,
    isInCentralKitchenOnlyPage,
    hasActiveCentralKitchen,
    authorizedActions,
    triggerExportIncoData,
    openCategoriesBatchUpdateModal,
    openSubCategoriesBatchUpdateModal,
    getProductsBatchUpdateTemplate,
    openBatchUpdateImportModal,
    canManageB2bCatalogs,
    triggerManageB2bCatalogsModal,
  } = props;

  const actions = [
    {
      id: 'Create',
      actionLabel: i18next.t('GENERAL.CREATE'),
      render: () =>
        !isUserManager(user) && (
          <Button
            buttonCustomStyle={{ fontWeight: 400 }}
            color={'blue'}
            fontSize={14}
            formatText={false}
            handleClick={triggerCreate}
            icon={'/images/inpulse/add-white-small.svg'}
            iconCustomStyle={{ width: '14px', height: '14px' }}
            label={i18next.t('GENERAL.CREATE')}
          />
        ),
      fixed: true,
      handleAction: () => {},
    },
  ];

  if (selectedItems.length === 0) {
    actions.push(
      {
        id: 'action-update-category',
        actionLabel: i18next.t('GENERAL.UPDATE_CATEGORIES'),
        isHidden: () => isUserManager(user) || !filteringOnActive,
        handleAction: () =>
          openCategoriesBatchUpdateModal(false, [], getManageCategoriesModalParams()),
        actionIcon: () => '/images/inpulse/gear-ipblack-small.svg',
      },
      {
        id: 'action-update-sub-category',
        actionLabel: i18next.t('GENERAL.UPDATE_SUB_CATEGORIES'),
        isHidden: () => isUserManager(user) || !filteringOnActive,
        handleAction: () =>
          openSubCategoriesBatchUpdateModal(false, [], getManageSubCategoriesModalParams()),
        actionIcon: () => '/images/inpulse/gear-ipblack-small.svg',
      },
      {
        id: 'export-list',
        actionLabel: i18next.t('ORDERS.ORDERS.LIST_EXPORT'),
        handleAction: () => triggerExport(),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      },
      {
        id: 'export-by-store',
        actionLabel: i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE'),
        handleAction: () => triggerExportByStore(),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
        isHidden: () => !hasVariants || isInCentralKitchenOnlyPage,
      },
      {
        id: 'csm-download-template',
        actionLabel: i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_BATCH_CREATE_ACTION'),
        handleAction: getProductBatchCreationTemplate,
        actionIcon: () =>
          !canDownloadProductTemplate(authorizedActions)
            ? '/images/inpulse/file-download-lmgrey-small.svg'
            : '/images/inpulse/file-download-black-small.svg',
        isHidden: () =>
          !filteringOnActive ||
          isInCentralKitchenOnlyPage ||
          !canDownloadProductTemplate(authorizedActions),
        // TO-DO MS4-LOCAL-CATALOGS: Use this code to handle master catalog rights
        // isDisabled: () => !canDownloadProductTemplate(authorizedActions),
        // disabledTooltipText: () => i18next.t('GENERAL.ONLY_ADMIN_RIGHT'),
      },
      {
        id: 'csm-download-template-central',
        actionLabel: i18next.t(
          'ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_BATCH_CENTRAL_KITCHEN_CREATE_ACTION',
        ),
        handleAction: () => getCentralKitchenProductBatchCreationTemplate(),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
        isHidden: () => !filteringOnActive || !isInCentralKitchenOnlyPage,
      },
      {
        id: 'csm-import-template',
        actionLabel: i18next.t('ADMIN.PRODUCTS.UPLOAD_TEMPLATE_BATCH_CREATE_ACTION'),
        handleAction: () => openProductsImportModal(),
        actionIcon: () =>
          !canImportProduct(authorizedActions)
            ? '/images/inpulse/file-upload-lmgrey-small.svg'
            : '/images/inpulse/file-upload-black-small.svg',
        isHidden: () =>
          !filteringOnActive || isInCentralKitchenOnlyPage || !canImportProduct(authorizedActions),
        // TO-DO MS4-LOCAL-CATALOGS: Use this code to handle master catalog rights
        // isDisabled: () => !canImportProduct(authorizedActions),
        // disabledTooltipText: () => i18next.t('GENERAL.ONLY_ADMIN_RIGHT'),
      },
      {
        id: 'csm-import-template-central',
        actionLabel: i18next.t(
          'ADMIN.PRODUCTS.UPLOAD_TEMPLATE_BATCH_CENTRAL_KITCHEN_CREATE_ACTION',
        ),
        handleAction: () => triggerImportCentralKitchenProductsScope(),
        actionIcon: () => '/images/inpulse/file-upload-black-small.svg',
        isHidden: () => !filteringOnActive || !isInCentralKitchenOnlyPage,
        isDisabled: () => !hasActiveCentralKitchen,
        disabledTooltipText: () =>
          i18next.t(
            'ADMIN.PRODUCTS.UPLOAD_TEMPLATE_BATCH_CENTRAL_KITCHEN_CREATE_ACTION_DISABLED_TOOLTIP',
          ),
      },
      {
        id: 'export-inco-data',
        actionLabel: i18next.t('ADMIN.PRODUCTS.EXPORT_INCO_DATA'),
        handleAction: () => triggerExportIncoData(),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      },
      {
        id: 'download-update-template',
        actionLabel: i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_BATCH_UPDATE_ACTION'),
        handleAction: getProductsBatchUpdateTemplate,
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
        isHidden: () =>
          !filteringOnActive ||
          isInCentralKitchenOnlyPage ||
          !canDownloadBatchUpdateProductTemplate(authorizedActions),
      },
      {
        id: 'import-update-template',
        actionLabel: i18next.t('ADMIN.PRODUCTS.IMPORT_TEMPLATE_BATCH_UPDATE_ACTION'),
        handleAction: openBatchUpdateImportModal,
        actionIcon: () => '/images/inpulse/file-upload-black-small.svg',
        isHidden: () =>
          !filteringOnActive ||
          isInCentralKitchenOnlyPage ||
          !canImportBatchUpdateProduct(authorizedActions),
      },
    );
  } else {
    const isSingleSelection = selectedItems.length === 1;

    actions.push(
      {
        id: 'assign-category',
        actionLabel: i18next.t('GENERAL.LIST_ACTION_ASSIGN_CATEGORY'),
        isHidden: () => isUserManager(user) || !filteringOnActive,
        handleAction: () =>
          openCategoriesBatchUpdateModal(true, selectedItems, getAssignCategoriesModalParams()),
        actionIcon: () => '/images/inpulse/link-black-small.svg',
      },
      {
        id: 'assign-sub-category',
        actionLabel: i18next.t('GENERAL.LIST_ACTION_ASSIGN_SUBCATEGORY'),
        isHidden: () => isUserManager(user) || !filteringOnActive,
        handleAction: () =>
          openSubCategoriesBatchUpdateModal(
            true,
            selectedItems,
            getAssignSubCategoriesModalParams(),
          ),
        actionIcon: () => '/images/inpulse/link-black-small.svg',
      },
      {
        id: 'assign-manage-b2b-catalogs',
        actionLabel: i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_MANAGE_ACTION'),
        isHidden: () => !filteringOnActive || !canManageB2bCatalogs,
        handleAction: (products) => triggerManageB2bCatalogsModal(products),
        actionIcon: () => '/images/inpulse/pen-black-small.svg',
      },
      {
        id: 'disable-products',
        actionLabel: isSingleSelection
          ? i18next.t('ADMIN.PRODUCTS.DISABLE_PRODUCT')
          : i18next.t('ADMIN.PRODUCTS.DISABLE_PRODUCTS'),
        isHidden: () => !filteringOnActive || (!isUserAdmin(user) && !userHasRoleInpulse(user)),
        handleAction: (products) => {
          const params = getDisableProductsModalParams(
            products,
            disableProducts,
            isInCentralKitchenOnlyPage,
          );

          openGenericModal(params);
        },
        actionIcon: () => '/images/inpulse/power-black-small.svg',
        disabledTooltipText: () =>
          isSingleSelection
            ? i18next.t('ADMIN.PRODUCTS.DISABLE_PRODUCTS_DISABLED_TOOLTIP_SINGLE')
            : i18next.t('ADMIN.PRODUCTS.DISABLE_PRODUCTS_DISABLED_TOOLTIP_MULTIPLE'),
      },
      {
        id: 'enable-one-product',
        actionLabel: () => i18next.t('ADMIN.PRODUCTS.ENABLE_PRODUCT'),
        isDisabled: () => isEnableActionDisabled(selectedItems),
        disabledTooltipText: () => i18next.t('ADMIN.PRODUCTS.ENABLED_DISABLE_TOOLTIP'),
        actionIcon: () =>
          isEnableActionDisabled(selectedItems)
            ? '/images/inpulse/power-lmgrey-small.svg'
            : '/images/inpulse/power-black-small.svg',
        isHidden: () =>
          !isSingleSelection ||
          filteringOnActive ||
          (!isUserAdmin(user) && !userHasRoleInpulse(user)),
        handleAction: (products) => triggerEnableProductModal(head(products)),
      },
      {
        id: 'enable-products',
        actionLabel: () => i18next.t('ADMIN.PRODUCTS.ENABLE_PRODUCTS'),
        isDisabled: () => isEnableActionDisabled(selectedItems),
        disabledTooltipText: () =>
          i18next.t('ADMIN.PRODUCTS.ENABLE_PRODUCTS_DISABLED_TOOLTIP_MULTIPLE'),
        actionIcon: () =>
          isEnableActionDisabled(selectedItems)
            ? '/images/inpulse/power-lmgrey-small.svg'
            : '/images/inpulse/power-black-small.svg',
        isHidden: () =>
          isSingleSelection ||
          filteringOnActive ||
          (!isUserAdmin(user) && !userHasRoleInpulse(user)),
        handleAction: (products) => triggerEnableProductsModal(products),
      },
      {
        id: 'export-products',
        actionLabel: i18next.t('ADMIN.PRODUCTS.EXPORT_SELECTION'),
        handleAction: (items) => triggerExport(items),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      },
      {
        id: 'export-by-store',
        actionLabel: i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE'),
        handleAction: (items) => triggerExportByStore(items),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
        isHidden: () => !hasVariants || isInCentralKitchenOnlyPage,
      },
      {
        id: 'export-inco-data',
        actionLabel: i18next.t('ADMIN.PRODUCTS.EXPORT_INCO_DATA'),
        handleAction: (items) => triggerExportIncoData(items),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      },
      {
        id: 'csm-delete-products',
        actionLabel: isSingleSelection
          ? i18next.t('ADMIN.PRODUCTS.DELETE_PRODUCT')
          : i18next.t('ADMIN.PRODUCTS.DELETE_PRODUCTS'),
        handleAction: (products) => {
          const params = getDeleteProductsModalParams(
            products,
            handleProductsDeletion,
            isInCentralKitchenOnlyPage,
          );

          openGenericModal(params);
        },
        actionIcon: () => '/images/inpulse/delete-ip-black-small.svg',
        isHighlighted: () => true,
        isHidden: () => !userHasRoleInpulse(user) || filteringOnActive,
      },
    );
  }

  return sortActionsList(actions);
};

export const getRowActions = (props) => {
  const {
    filteringOnActive,
    user,
    openGenericModal,
    handleProductsDeletion,
    disableProducts,
    triggerEnableProductModal,
    isInCentralKitchenOnlyPage,
    openCategoriesBatchUpdateModal,
    openSubCategoriesBatchUpdateModal,
  } = props;

  const rowActions = [
    {
      id: 'assign-category',
      actionLabel: () => i18next.t('GENERAL.LIST_ACTION_ASSIGN_CATEGORY'),
      isHidden: () => isUserManager(user) || !filteringOnActive,
      handleAction: (product) =>
        openCategoriesBatchUpdateModal(true, [product], getAssignCategoriesModalParams()),
      actionIcon: () => '/images/inpulse/link-black-small.svg',
    },
    {
      id: 'assign-sub-category',
      actionLabel: () => i18next.t('GENERAL.LIST_ACTION_ASSIGN_SUBCATEGORY'),
      isHidden: () => isUserManager(user) || !filteringOnActive,
      handleAction: (product) =>
        openSubCategoriesBatchUpdateModal(true, [product], getAssignSubCategoriesModalParams()),
      actionIcon: () => '/images/inpulse/link-black-small.svg',
    },
    {
      id: 'disable-only-one',
      actionLabel: () => i18next.t('ADMIN.PRODUCTS.DISABLE_PRODUCT'),
      isHidden: () => !filteringOnActive || (!isUserAdmin(user) && !userHasRoleInpulse(user)),
      handleAction: (product) => {
        const params = getDisableProductsModalParams(
          [product],
          disableProducts,
          isInCentralKitchenOnlyPage,
        );

        openGenericModal(params);
      },
      actionIcon: () => '/images/inpulse/power-black-small.svg',
      disabledTooltipText: () =>
        i18next.t('ADMIN.PRODUCTS.DISABLE_PRODUCTS_DISABLED_TOOLTIP_SINGLE'),
    },
    {
      id: 'enable-one-product',
      actionLabel: () => i18next.t('ADMIN.PRODUCTS.ENABLE_PRODUCT'),
      isDisabled: (product) => isEnableActionDisabled([product]),
      disabledTooltipText: () => i18next.t('ADMIN.PRODUCTS.ENABLED_DISABLE_TOOLTIP'),
      actionIcon: (product) =>
        isEnableActionDisabled([product])
          ? '/images/inpulse/power-lmgrey-small.svg'
          : '/images/inpulse/power-black-small.svg',
      isHidden: () => filteringOnActive || (!isUserAdmin(user) && !userHasRoleInpulse(user)),
      handleAction: (product) => triggerEnableProductModal(product),
    },
    {
      id: 'delete-product',
      actionLabel: () => i18next.t('ADMIN.PRODUCTS.DELETE_PRODUCT'),
      handleAction: (product) => {
        const params = getDeleteProductsModalParams(
          [product],
          handleProductsDeletion,
          isInCentralKitchenOnlyPage,
        );

        openGenericModal(params);
      },
      actionIcon: () => '/images/inpulse/delete-ip-black-small.svg',
      isHighlighted: () => true,
      isHidden: () => !userHasRoleInpulse(user) || filteringOnActive,
    },
  ];

  return rowActions;
};

export const getEmptyStateActions = (props) => {
  const {
    user,
    getProductBatchCreationTemplate,
    getCentralKitchenProductBatchCreationTemplate,
    openProductsImportModal,
    triggerImportCentralKitchenProductsScope,
    isInCentralKitchenOnlyPage,
    hasActiveCentralKitchen,
    authorizedActions,
    openCategoriesBatchUpdateModal,
    openSubCategoriesBatchUpdateModal,
  } = props;

  const actions = [
    {
      label: i18next.t('GENERAL.UPDATE_CATEGORIES'),
      isHidden: isUserManager(user),
      handleClick: () =>
        openCategoriesBatchUpdateModal(false, [], getManageCategoriesModalParams()),
      icon: '/images/inpulse/gear-ipblack-small.svg',
    },
    {
      label: i18next.t('GENERAL.UPDATE_SUB_CATEGORIES'),
      isHidden: isUserManager(user),
      handleClick: () =>
        openSubCategoriesBatchUpdateModal(false, [], getManageSubCategoriesModalParams()),
      icon: '/images/inpulse/gear-ipblack-small.svg',
    },
    {
      label: i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_BATCH_CREATE_ACTION'),
      handleClick: () => getProductBatchCreationTemplate(),
      isHighlighted: () => true,
      icon: '/images/inpulse/file-download-white-small.svg',
      isHidden: isInCentralKitchenOnlyPage || !canDownloadProductTemplate(authorizedActions),
    },
    {
      label: i18next.t('ADMIN.PRODUCTS.UPLOAD_TEMPLATE_BATCH_CREATE_ACTION'),
      handleClick: () => openProductsImportModal(),
      isHighlighted: () => true,
      icon: '/images/inpulse/file-upload-white-small.svg',
      isHidden: isInCentralKitchenOnlyPage || !canImportProduct(authorizedActions),
    },
    {
      label: i18next.t('ADMIN.PRODUCTS.DOWNLOAD_TEMPLATE_BATCH_CENTRAL_KITCHEN_CREATE_ACTION'),
      handleClick: () => getCentralKitchenProductBatchCreationTemplate(),
      isHighlighted: () => true,
      icon: '/images/inpulse/file-download-white-small.svg',
      isHidden: !isInCentralKitchenOnlyPage,
    },
    {
      label: i18next.t('ADMIN.PRODUCTS.UPLOAD_TEMPLATE_BATCH_CENTRAL_KITCHEN_CREATE_ACTION'),
      handleClick: () => triggerImportCentralKitchenProductsScope(),
      isHighlighted: () => true,
      icon: '/images/inpulse/file-upload-white-small.svg',
      isHidden: !isInCentralKitchenOnlyPage,
      isDisabled: !hasActiveCentralKitchen,
    },
  ];

  return sortActionsList(actions);
};
